import { CommandMenu, CommandMenuPage, IconButton, LuIcon, Text } from "@overmap-ai/blocks"
import { type Issue, type IssueType, type Stored, UNTITLED_ISSUE_TITLE } from "@overmap-ai/common"
import { useScanner } from "@overmap-ai/forms"
import { useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement, useCallback, useMemo, useState } from "react"

import { issueTypeFilterCallback } from "../../filtering/utils"
import { ISSUE_FUSE_OPTIONS } from "../../fuse/issues"
import { useChangeEventHandler, useFuse } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import type { IssueAndIdentifiers } from "../../typings"
import { IssueTypeIcon } from "../Icons/IssueTypeIcon"
import { IssueIndexLabel } from "../IndexLabels/IssueIndex"
import type { ExtendableCommandMenuProps } from "./typings"

interface IssueCommandMenuProps extends Omit<ExtendableCommandMenuProps, "title"> {
	children?: ReactElement
	issuesAndIdentifiers: IssueAndIdentifiers[]
	issueTypes: Stored<IssueType>[]
	onIssueSelect?: (issue: Stored<Issue>) => void
	onCreateIssue?: (issueType: Stored<IssueType>) => void
}

export const IssueCommandMenu = memo<IssueCommandMenuProps>((props) => {
	const {
		children,
		open,
		defaultOpen,
		onOpenChange, // oxlint-disable-line unbound-method
		modal,
		issuesAndIdentifiers,
		issueTypes,
		onIssueSelect,
		onCreateIssue,
		...rest
	} = props

	const openScanner = useScanner()

	const [issueFilterValue, setIssueFilterValue] = useState("")

	const filteredIssues = useFuse<IssueAndIdentifiers>(issuesAndIdentifiers, issueFilterValue, ISSUE_FUSE_OPTIONS)

	const [filteredIssueTypes, issueTypeFilterValue, setIssueTypeFilterValue] = useTextFilter(
		issueTypes,
		issueTypeFilterCallback,
	)

	const issuesLimited = useMemo(() => {
		return filteredIssues.slice(0, 10)
	}, [filteredIssues])

	const handleIssueFilterChange = useChangeEventHandler(setIssueFilterValue)
	const handleIssueTypeFilterChange = useChangeEventHandler(setIssueTypeFilterValue)

	const handleScan = useCallback(() => {
		openScanner((close) => ({
			onDecodeResult: (result) => {
				setIssueFilterValue(result.rawValue.trim())
				close()
			},
			onClose: close,
		}))
	}, [openScanner, setIssueFilterValue])

	return (
		<CommandMenu.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
			{!!children && <CommandMenu.Trigger asChild>{children}</CommandMenu.Trigger>}
			<CommandMenu.Overlay />
			<CommandMenu.Content title="Issue command menu" {...rest}>
				<CommandMenu.Pages defaultPage="issues">
					<CommandMenuPage page="issues">
						<div className="flex items-center justify-between gap-2">
							<CommandMenu.Input
								placeholder="Search"
								value={issueFilterValue}
								onChange={handleIssueFilterChange}
							/>
							<IconButton onClick={handleScan} variant="soft" className="mr-2" accentColor="base">
								<LuIcon icon={modelIconMapping.scan} />
							</IconButton>
						</div>
						<CommandMenu.Separator />
						<CommandMenu.Scroll>
							{issuesLimited.length > 0 ? (
								<>
									<CommandMenu.Group>
										<CommandMenu.GroupLabel>Issues</CommandMenu.GroupLabel>
										{issuesLimited.map(({ issue }) => {
											return (
												<CommandMenu.Item
													key={issue.uuid}
													onSelect={() => onIssueSelect?.(issue)}
												>
													<IssueTypeIcon issueTypeId={issue.issue_type} />
													<IssueIndexLabel issueId={issue.uuid} />
													<Text className="truncate" accentColor="base">
														{issue.title || UNTITLED_ISSUE_TITLE}
													</Text>
												</CommandMenu.Item>
											)
										})}
									</CommandMenu.Group>
								</>
							) : (
								<CommandMenu.Empty>No issues.</CommandMenu.Empty>
							)}
						</CommandMenu.Scroll>
						<CommandMenu.Separator />
						<CommandMenu.PageTriggerItem page="issue-types">
							<LuIcon icon={modelIconMapping.add} /> Create
						</CommandMenu.PageTriggerItem>
					</CommandMenuPage>

					<CommandMenuPage page="issue-types">
						<CommandMenu.Input
							placeholder="Search"
							value={issueTypeFilterValue}
							onChange={handleIssueTypeFilterChange}
						/>
						<CommandMenu.Separator />
						<CommandMenu.Scroll>
							{filteredIssueTypes.length > 0 ? (
								<>
									<CommandMenu.Group>
										<CommandMenu.GroupLabel>Issue types</CommandMenu.GroupLabel>
										{filteredIssueTypes.map((issueType) => {
											return (
												<CommandMenu.Item
													key={issueType.uuid}
													onSelect={() => onCreateIssue?.(issueType)}
												>
													<IssueTypeIcon issueTypeId={issueType.uuid} />
													<Text className="truncate">{issueType.name}</Text>
												</CommandMenu.Item>
											)
										})}
									</CommandMenu.Group>
								</>
							) : (
								<CommandMenu.Empty>No issue types.</CommandMenu.Empty>
							)}
						</CommandMenu.Scroll>
					</CommandMenuPage>
				</CommandMenu.Pages>
			</CommandMenu.Content>
		</CommandMenu.Root>
	)
})
IssueCommandMenu.displayName = "IssueCommandMenu"
